/* Shared by the hero and curated gallery dialogs. Fit is relative to the visible stage. */
(() => {
  if (customElements.get('ona-image-viewer')) return;
  const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value));
  class OnaImageViewer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.closest('dialog');
      this.stage = this.querySelector('[data-viewer-stage]');
      this.image = this.querySelector('img');
      this.out = this.querySelector('[data-viewer-out]');
      this.plus = this.querySelector('[data-viewer-in]');
      this.level = this.querySelector('[data-viewer-level]');
      this.pointers = new Map();
      this.scale = 1; this.x = this.y = 0;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-viewer-in]')) this.zoom(this.scale * 1.5);
        if (event.target.closest('[data-viewer-out]')) this.zoom(this.scale / 1.5);
        if (event.target.closest('[data-viewer-fit]')) this.fit();
      }, { signal });
      this.stage.addEventListener('dblclick', event => {
        event.preventDefault();
        this.zoom(this.scale > 1 ? 1 : 2, event.clientX, event.clientY);
      }, { signal });
      this.stage.addEventListener('wheel', event => {
        event.preventDefault();
        this.zoom(this.scale * Math.exp(-event.deltaY * .002), event.clientX, event.clientY);
      }, { passive:false, signal });
      this.stage.addEventListener('pointerdown', event => {
        if (event.button !== 0) return;
        this.stage.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x:event.clientX, y:event.clientY });
        this.stage.classList.add('is-dragging');
      }, { signal });
      this.stage.addEventListener('pointermove', event => {
        const previous = this.pointers.get(event.pointerId);
        if (!previous) return;
        const before = [...this.pointers.values()];
        this.pointers.set(event.pointerId, { x:event.clientX, y:event.clientY });
        const after = [...this.pointers.values()];
        if (after.length === 2) {
          const distance = points => Math.hypot(points[0].x-points[1].x, points[0].y-points[1].y);
          const oldDistance = distance(before);
          if (oldDistance) this.zoom(this.scale * distance(after) / oldDistance, (after[0].x+after[1].x)/2, (after[0].y+after[1].y)/2);
        } else if (after.length === 1 && this.scale > 1) {
          this.x += event.clientX - previous.x; this.y += event.clientY - previous.y;
          this.render();
        }
      }, { signal });
      const release = event => {
        this.pointers.delete(event.pointerId);
        if (!this.pointers.size) this.stage.classList.remove('is-dragging');
      };
      ['pointerup','pointercancel','lostpointercapture'].forEach(name => this.stage.addEventListener(name, release, { signal }));
      this.dialog.addEventListener('keydown', event => {
        if (event.key === '+' || event.key === '=') { event.preventDefault(); this.zoom(this.scale * 1.5); }
        if (event.key === '-') { event.preventDefault(); this.zoom(this.scale / 1.5); }
        if (event.key === '0') { event.preventDefault(); this.fit(); }
        if (this.scale > 1 && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key)) {
          event.preventDefault();
          this.x += event.key === 'ArrowLeft' ? 48 : event.key === 'ArrowRight' ? -48 : 0;
          this.y += event.key === 'ArrowUp' ? 48 : event.key === 'ArrowDown' ? -48 : 0;
          this.render();
        }
      }, { signal });
      this.image.addEventListener('load', () => this.measure(), { signal });
      this.dialog.addEventListener('close', () => { this.pointers.clear(); this.fit(); }, { signal });
      this.openObserver = new MutationObserver(() => {
        if (!this.dialog.open) return;
        this.fit();
        // Use the original Shopify file, without the responsive thumbnail width cap.
        try {
          const url = new URL(this.image.src, location.origin);
          if (url.hostname.endsWith('shopify.com')) {
            url.searchParams.delete('width'); url.searchParams.delete('height');
            this.image.removeAttribute('srcset'); this.image.removeAttribute('sizes');
            this.image.src = url.href;
          }
        } catch { /* Keep the existing safe image source. */ }
        requestAnimationFrame(() => this.measure());
      });
      this.openObserver.observe(this.dialog, { attributes:true, attributeFilter:['open'] });
      this.resizeObserver = new ResizeObserver(() => { if (this.dialog.open) this.measure(); });
      this.resizeObserver.observe(this.stage);
    }
    measure() {
      if (!this.image.naturalWidth || !this.stage.clientWidth || !this.stage.clientHeight) return;
      const fit = Math.min(this.stage.clientWidth / this.image.naturalWidth, this.stage.clientHeight / this.image.naturalHeight, 1);
      this.baseWidth = this.image.naturalWidth * fit;
      this.baseHeight = this.image.naturalHeight * fit;
      this.image.style.width = `${this.baseWidth}px`; this.image.style.height = `${this.baseHeight}px`;
      this.render();
    }
    fit() { this.scale = 1; this.x = this.y = 0; this.measure(); this.render(); }
    zoom(value, clientX, clientY) {
      const next = Math.max(1, Math.min(4, value));
      const rect = this.stage.getBoundingClientRect();
      const originX = clientX == null ? 0 : clientX - rect.left - rect.width/2;
      const originY = clientY == null ? 0 : clientY - rect.top - rect.height/2;
      this.x = originX - (originX-this.x) * next/this.scale;
      this.y = originY - (originY-this.y) * next/this.scale;
      this.scale = next;
      this.render();
    }
    render() {
      this.x = clamp(this.x, Math.max(0, ((this.baseWidth || 0)*this.scale-this.stage.clientWidth)/2));
      this.y = clamp(this.y, Math.max(0, ((this.baseHeight || 0)*this.scale-this.stage.clientHeight)/2));
      this.image.style.transform = `translate(-50%,-50%) translate(${this.x}px,${this.y}px) scale(${this.scale})`;
      this.stage.classList.toggle('is-zoomed', this.scale > 1.001);
      this.level.value = `${Math.round(this.scale*100)}%`;
      this.out.disabled = this.scale <= 1.001;
      this.plus.disabled = this.scale >= 3.999;
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.openObserver?.disconnect(); this.resizeObserver?.disconnect();
    }
  }
  customElements.define('ona-image-viewer', OnaImageViewer);
})();
